import { createContext, useState, useEffect, useContext, useRef } from 'react'

import { postStudentChat, studyRunState, newClientRequestId } from '../../../service/studentChatService'
import { studyRunNote, testSummary } from '../study/studyRunNote'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { CodeContext } from './CodeContext'
import { TerminalContext } from './TerminalContext' 
import { TelemetryContext } from './TelemetryContext' 

import { 
    getMessagesBetweenUserAndAI, 
    saveMessage,   
    AI_AGENT_ID    
} from '../service/workspaceMessageService'

import { buildContextualPrompt } from '../utils/AIChat/promptContextBuilder'
import { prepareOpenAIMessages, prepareStudyMessages } from '../utils/AIChat/memoryManagement'
import { summarizeRecentInteractions, getLatestVisualContext } from '../utils/AIChat/recentInteractionSummary'
import { getGuiSessionConfig } from '../config/guiSessionConfig'
import { getSessionLanguage } from '../service/languageConfig'
import { isStudySession } from '../study/studyConfig'

import { CHAT_EVENTS } from '../constants/telemetryEvents'

export const ChatContext = createContext()

// getInitialFinalSystemPrompt/getInitialFinalCSSSystemPrompt (systemPrompt.js) both instruct
// the model to reply with raw code and no markdown fence, since the whole exercise depends
// on copying the ENTIRE response verbatim being the correct paste - but GPT-4o doesn't
// reliably comply. Two observed failure shapes, both confirmed live (including in real
// student sessions, e.g. a reply that opened with "To make the layout responsive, we
// should..." before a ```css fence, which the student then couldn't just paste in): the
// whole reply wrapped in one fence, and - not caught by the original version of this
// function, which only matched a fence spanning the entire trimmed text - a lead-in
// sentence and/or trailing explanation around the fence. In this exercise type a fenced
// block only ever shows up when the model is (however imperfectly) trying to hand back the
// real answer - the system prompt tells it to include no code at all otherwise - so once a
// fence appears anywhere, its contents are treated as the entire real answer and everything
// else is dropped, rather than pasting commentary and fence markers into the student's
// editor along with the code.
const stripSurroundingCodeFence = (text) => {
    const wrapped = /^```[a-zA-Z]*[ \t]*\r?\n([\s\S]*?)\r?\n?```\s*$/.exec(text.trim())
    if (wrapped) return wrapped[1]
    const anyFence = /```[a-zA-Z]*[ \t]*\r?\n([\s\S]*?)\r?\n?```/.exec(text)
    return anyFence ? anyFence[1] : text
}

// clientOnly: shown by this page, never said by the tutor - a study condition's requests
// leave such lines out of the history (memoryManagement.js prepareStudyMessages).
const WELCOME_MESSAGE = {
    id: 'welcome-msg',
    content: "Hey I am your AI assistant, feel free to ask me questions if you are stuck!",
    sender_id: AI_AGENT_ID,
    timestamp: new Date().toISOString(),
    clientOnly: true
}

// ─── Behavioral Classification ──────────────────────────────

const BEHAVIORAL_PROMPTS = {
  COLD_START: `[BEHAVIORAL CONTEXT: This is the student's first time asking for help. They have not written or run any code yet. Give them something concrete to start with — a pseudocode scaffold with blanks to fill in. End your response with a single sentence starting with "Try:" that gives them that pseudocode starting point so they know what to write next.]`,

  PASSIVE: `[BEHAVIORAL CONTEXT: This student received help but has not written or run any code since. Try a different angle from your last response — do not repeat the same explanation. Do NOT escalate your scaffolding level — stay at the same level but approach it differently. If the student asks "how to fix" or requests the answer directly, treat this the same as the system prompt rule — acknowledge, redirect, and re-ask with pseudocode. If the student has just completed a step and is asking what to do next, start at Level 1 for the new step. If the question is purely conceptual, answer it naturally. Otherwise, end your response with a single sentence starting with "Try:" that gives them one concrete next action using pseudocode with blanks if code is involved.]`,  
  
  MODERATE: `[BEHAVIORAL CONTEXT: This student wrote some code but has not tested it. Do not give more explanation. If their current code would produce meaningful output (e.g. contains a print statement, an expression, or enough logic to show a result), end your response with a single sentence starting with "Try:" telling them to run it. If running it would produce no output, end with a "Try:" that asks them to add a specific print statement first so they can see what their code is doing.]`,

  ACTIVE: `[BEHAVIORAL CONTEXT: This student has been coding, testing, and hitting errors. They are engaged and struggling. Recognize their effort. Name the specific error and the misconception behind it so they can read errors independently. Escalate to Level 2 or 3 immediately. End your response with a single sentence starting with "Try:" giving them the targeted fix as a pseudocode pattern with blanks.]`,

  INTENSIVE: `[BEHAVIORAL CONTEXT: This student has run their code many times and keeps hitting errors. They do not need encouragement — they need to be unblocked. Name the concept or method they need, explain briefly why their current approach fails, and escalate to Level 3 immediately. End your response with a single sentence starting with "Try:" that gives them the precise pseudocode pattern they need, with blanks for the specific values.]`,
}

function classifyPreQueryBehavior(events, windowStart, queryTimestamp, hasPriorAiResponse) {
  const windowEvents = events.filter(e => e.timestamp >= windowStart && e.timestamp < queryTimestamp)
  const edits  = windowEvents.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_DELETE').length
  const runs   = windowEvents.filter(e => e.type === 'TERMINAL_RUN').length
  const errors = windowEvents.filter(e => e.type === 'TERMINAL_ERROR').length

  // Zero activity
  if (edits === 0 && runs === 0) {
    return hasPriorAiResponse
      ? { key: 'PASSIVE', prompt: BEHAVIORAL_PROMPTS.PASSIVE }
      : { key: 'COLD_START', prompt: BEHAVIORAL_PROMPTS.COLD_START }
  }

  // Wrote code but never ran, or ran with no errors
  if (runs === 0 || (runs >= 1 && errors === 0))
    return { key: 'MODERATE', prompt: BEHAVIORAL_PROMPTS.MODERATE }

  // Extreme effort — many runs, many errors
  if (runs >= 3 && errors >= 3)
    return { key: 'INTENSIVE', prompt: BEHAVIORAL_PROMPTS.INTENSIVE }

  // Tested, hit errors (but not extreme)
  return { key: 'ACTIVE', prompt: BEHAVIORAL_PROMPTS.ACTIVE }
}

// ── Provider ───────────────────────────────────────────────
export const ChatProvider = ({ children }) => {
    const { currentUser } = useContext(AuthContext)
    const { session } = useContext(SessionContext)

    const { logTelemetryEvent, getAllEvents } = useContext(TelemetryContext)

    const [messages, setMessages] = useState([])
    const [isAiProcessing, setIsAiProcessing] = useState(false)
    const [lastResponseTime, setLastResponseTime] = useState(null)
    
    const { codeRef } = useContext(CodeContext) 
    
    // lastRunInfo ({ runId, codeHash, failingTests, errorName, at }) describes the latest run
    // for a study condition's requests; undefined on a page without it (studyRunState).
    const { testResults, terminalOutput, lastRunInfo } = useContext(TerminalContext)
    // A study condition's test summary at the student's previous question on this page
    // (studyRunNote): undefined until their first question.
    const testsAtLastQuestionRef = useRef(undefined)


    // Fetch message history (always prepend welcome message)
    useEffect(() => {
        if (!currentUser?.id || !session?.id) return 
        
        const fetchHistory = async () => {
            try {
                const history = await getMessagesBetweenUserAndAI(currentUser.id, session.id)
                
                if (history && history.length > 0) {
                    setMessages([WELCOME_MESSAGE, ...history])
                    const lastAiMsg = [...history]
                        .reverse()
                        .find(m => m.sender_id === AI_AGENT_ID)
                    if (lastAiMsg) {
                        setLastResponseTime(new Date(lastAiMsg.timestamp).getTime())
                    }
                } else {
                    setMessages([WELCOME_MESSAGE])
                }
            } catch (err) { console.error(err) }
        }
        fetchHistory()
    }, [currentUser?.id, session?.id])

    // manualAttachment: { kind: 'screenshot'|'burst', frames: [{screenshot, ...}] } - set when the
    // student clicked the screenshot/record button in ChatWindow. Distinct from the background
    // visualTraceEnabled/aiVisualContextEnabled capture: this is a deliberate, visible action the
    // student took for this one message, so it's sent to the AI regardless of aiVisualContextEnabled.
    // learnMoreConcept: set when the offloading strip's "Learn more" sent this (see saveMessage).
    const sendMessage = async (userText, manualAttachment, { learnMoreConcept } = {}) => {
        if (!userText.trim() || isAiProcessing) return

        setIsAiProcessing(true)
        const startTime = Date.now() 

        const currentCodeSnapshot = codeRef.current

        // A classroom-study condition (study/studyConfig.js): the server builds its tutor's
        // prompt and logs the exchange under this request's id, which the chat telemetry
        // below carries too so the two can be matched. Other sessions log as before.
        const isStudy = isStudySession(session)
        const clientRequestId = isStudy ? newClientRequestId() : null
        const studyTelemetry = isStudy ? { client_request_id: clientRequestId } : {}

        // 1. Log Query Intent
        logTelemetryEvent(CHAT_EVENTS.QUERY, {
            text: userText,
            length: userText.length,
            timestamp: startTime,
            ...studyTelemetry
        })

        // attachmentThumbnail/attachmentCount are client-side only, for display in this live
        // session's chat history - never persisted (saveMessage below only ever sends
        // userText), matching the rest of this app's "images are sent to the LLM, never
        // stored" design. Without this the image the student just attached and sent visibly
        // vanished the moment the message left the composer, with nothing in the chat log to
        // show it was ever there - confusing since the AI's reply clearly responds to an
        // image the student can no longer see.
        const newUserMsg = {
            id: Date.now(),
            content: userText,
            sender_id: currentUser.id,
            timestamp: new Date().toISOString(),
            attachmentThumbnail: manualAttachment?.frames?.[0]?.screenshot || null,
            attachmentCount: manualAttachment?.frames?.length || 0,
        }

        // Update UI immediately
        const updatedMessages = [...messages, newUserMsg]
        setMessages(updatedMessages)

        console.log("GROUP", session?.experiment_group) // Debug log

        try {
            saveMessage(
                userText,
                currentUser.id,
                AI_AGENT_ID,
                session.id,
                currentCodeSnapshot,
                { learnMoreConcept }
            ).catch(err => console.error("User Message Save Failed:", err))

            const guiConfig = getGuiSessionConfig(session)
            const allEvents = getAllEvents()
            const recentInteractions = summarizeRecentInteractions(allEvents)
            const visualContext = guiConfig?.visualTraceEnabled ? getLatestVisualContext(allEvents) : null
            // A study condition's tutor also hears whether the code changed since the run its
            // results come from, and how the results moved since the previous question.
            const runNote = isStudy
                ? studyRunNote({ code: currentCodeSnapshot, lastRunInfo, testResults, earlier: testsAtLastQuestionRef.current })
                : ''
            if (isStudy) testsAtLastQuestionRef.current = testSummary(testResults)

            const fullPrompt = buildContextualPrompt(
                userText,
                currentCodeSnapshot,
                testResults,
                terminalOutput,
                // Same fallback TaskDescription.jsx already uses for display - without it, a
                // GUI session with no instructor-edited assignment (session.task null, still
                // on the phase default shown on-screen via guiConfig.assignment) silently told
                // the AI "No task description provided" while the student saw a real one.
                session.task || guiConfig?.assignment,
                session,
                recentInteractions,
                visualContext,
                runNote
            )

            // 2. Inject behavioral context for treatment group only
            let enrichedPrompt = fullPrompt

            // stick with baseline prompt for now
            if (false) {
                const windowStart = lastResponseTime || 
                    (session?.startTime ? new Date(session.startTime).getTime() : 0)
                
               const behavioral = classifyPreQueryBehavior(
                    getAllEvents(),
                    windowStart,
                    startTime,
                    !!lastResponseTime
                )

                enrichedPrompt = behavioral.prompt + '\n\n' + fullPrompt

                // Log classification for analysis
                logTelemetryEvent(CHAT_EVENTS.QUERY, {
                    behavioral_classification: behavioral.key,
                    timestamp: Date.now()
                })
            }

            const manualImageDataUrls = manualAttachment?.frames
                ?.map((f) => f.screenshot)
                .filter(Boolean)

            // Without this, GPT-4o tends to see the word "recording" in the student's own message
            // and reflexively reply that it can't process video - even though what's actually
            // attached below are ordinary still images, which it handles fine.
            const isWorkspaceCaptureLayout = guiConfig?.layout === 'compare' || guiConfig?.layout === 'compare-image' || guiConfig?.layout === 'compare-js'
            if (manualImageDataUrls?.length === 1) {
                enrichedPrompt += isWorkspaceCaptureLayout
                    ? `\n\n[The student attached a screenshot of a region of their workspace they selected themselves below - it may show surrounding panels/editor, not just the rendered output in isolation.]`
                    : `\n\n[The student attached a screenshot of their rendered output below.]`
            } else if (manualImageDataUrls?.length > 1) {
                enrichedPrompt += `\n\n[The student attached ${manualImageDataUrls.length} sequential screenshots below, taken about 1 second apart over roughly ${manualImageDataUrls.length} seconds, showing how their rendered output looked over that time. These are still images, not a video file - look at them in order like a flipbook.]`
            }

            console.log("Final Prompt Sent to GPT:", enrichedPrompt) // Debug log
            const backgroundImageDataUrl = (guiConfig?.aiVisualContextEnabled && visualContext?.screenshot) || undefined
            const imageDataUrls = (manualImageDataUrls?.length ? manualImageDataUrls : null)
                || (backgroundImageDataUrl ? [backgroundImageDataUrl] : undefined)
            // Same guiConfig-first pattern as promptContextBuilder.js's language detection -
            // without it, the system prompt (see memoryManagement.js) always fell back to Python
            // for every GUI session, regardless of whether it was HTML/CSS/JavaScript.
            const language = guiConfig ? guiConfig.phase : getSessionLanguage(session.id)

            // A study condition sends no system prompt and the question once (`messages` is
            // the chat before it) - see memoryManagement.js.
            const apiPayload = isStudy
                ? prepareStudyMessages(messages, enrichedPrompt, imageDataUrls)
                : prepareOpenAIMessages(updatedMessages, enrichedPrompt, language, imageDataUrls)

            // Send to GPT
            // Goes through /gpt/studentChat so the session's AI Chat Policy is applied
            // server-side (see studentChatService.js); same { role, content } response.
            // A study condition also says who is asking and how their latest run went.
            const gptReply = await postStudentChat(session, apiPayload, isStudy ? {
                userId: currentUser.id,
                studyState: {
                    ...studyRunState({ lastRunInfo, testResults, testList: session.test_list, terminalOutput }),
                    clientRequestId
                }
            } : undefined)
            const aiContent = (language === 'html-target' || language === 'css-target' || language === 'js-target')
                ? stripSurroundingCodeFence(gptReply.content)
                : gptReply.content
            const endTime = Date.now()

            // 3. Track last response time for next window
            setLastResponseTime(endTime)

            // Log AI Response
            logTelemetryEvent(CHAT_EVENTS.RESPONSE, {
                content: aiContent,           
                length: aiContent.length,
                latency_ms: endTime - startTime,
                timestamp: endTime,
                ...(isStudy ? { ...studyTelemetry, interaction_id: gptReply.interactionId ?? null } : {})
            })

            const newAiMsg = {
                id: Date.now() + 1,
                content: aiContent,
                sender_id: AI_AGENT_ID, 
                timestamp: new Date().toISOString()
            }
            setMessages(prev => [...prev, newAiMsg])

            // Save AI Response to DB 
            saveMessage(aiContent, AI_AGENT_ID, currentUser.id, session.id)
                .catch(err => console.error("AI Message Save Failed:", err))

        } catch (err) {
            console.error("Chat Error:", err)
            
            // Log System Failure
            // A study condition's error also says which request failed and how (status null:
            // no answer came at all, e.g. after the 90 s timeout), and why the server refused it
            // (reason, e.g. 'ai-locked' or 'not-placed': studyTutor/access.js).
            logTelemetryEvent(CHAT_EVENTS.ERROR, {
                error: err.message || "Unknown API Error",
                ...(isStudy ? { ...studyTelemetry, status: err?.response?.status ?? null, reason: err?.response?.data?.reason ?? null } : {})
            })

            // Tell the student instead of leaving their question unanswered. Shown only,
            // never saved: it is not something the tutor said. (The AI route now fails the
            // request when the AI provider errors, rather than replying with the raw error.)
            // A message the study tutor refused for its length (reason 'too-long': over its
            // word limit) gets the server's own words, which say what to do.
            const refusedAsTooLong = err?.response?.data?.reason === 'too-long' && typeof err.response.data.message === 'string'
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                content: refusedAsTooLong
                    ? err.response.data.message
                    : "Sorry, the AI assistant couldn't answer right now. Please try again.",
                sender_id: AI_AGENT_ID,
                timestamp: new Date().toISOString(),
                clientOnly: true
            }])
        } finally {
            setIsAiProcessing(false)
        }
    }

    return (
        <ChatContext.Provider value={{ messages, sendMessage, isAiProcessing }}>
            {children}
        </ChatContext.Provider>
    )
}
